/**
 * Iter 225 — Page Stats Messagerie.
 *
 * Affiche : volume, vitesse de réponse, IA, NPS proxy, top intents, heatmap, top senders.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Activity, Zap, Bot, Smile, Frown, Clock, MessageSquare, RefreshCw, TrendingUp,
} from "lucide-react";
import PageHeader from "../components/PageHeader";

const PERIODS = [
  { value: "7d", label: "7 jours" },
  { value: "30d", label: "30 jours" },
  { value: "90d", label: "90 jours" },
  { value: "all", label: "Tout" },
];

const INTENT_COLORS = {
  faq_simple: "#22c55e",
  technical_question: "#3b82f6",
  complaint: "#f43f5e",
  qualified_prospect: "#f59e0b",
  spam: "#64748b",
  other: "#94a3b8",
};

const INTENT_LABELS = {
  faq_simple: "FAQ simple",
  technical_question: "Question technique",
  complaint: "Réclamation",
  qualified_prospect: "Prospect qualifié",
  spam: "Spam",
  other: "Autre",
};

const CHANNEL_BG = { whatsapp: "#25d366", messenger: "#0084ff", instagram: "#e1306c" };

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${Math.round(seconds / 86400)}j`;
}

export default function MessagingStatsPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [intents, setIntents] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [o, i, h, s] = await Promise.all([
        api.get(`/meta/stats/overview?period=${period}`),
        api.get(`/meta/stats/intents?period=${period}`),
        api.get(`/meta/stats/heatmap?period=${period}`),
        api.get(`/meta/stats/top-senders?period=${period}&limit=10`),
      ]);
      setOverview(o.data);
      setIntents(i.data?.items || []);
      setHeatmap(h.data);
      setSenders(s.data?.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, period]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="messaging-stats-page">
      <PageHeader
        title="Stats Messagerie"
        subtitle="Analytics avancées sur vos conversations WhatsApp · Messenger · Instagram"
        icon={<Activity />}
      />

      {/* Period selector */}
      <div className="flex items-center justify-between mt-4 mb-5">
        <div className="flex gap-1 bg-[#14101c] border border-white/10 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`text-[11px] px-3 py-1 rounded transition-colors ${period === p.value ? "bg-violet-500 text-white" : "text-slate-400 hover:text-white"}`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button onClick={reload} className="text-slate-500 hover:text-white" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* KPIs row */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <KpiCard
            icon={MessageSquare} color="#3b82f6"
            label="Messages" value={overview.volume.total_messages}
            sub={`${overview.volume.inbound} entrants · ${overview.volume.outbound} sortants`}
          />
          <KpiCard
            icon={Zap} color="#22c55e"
            label="Réponses < 5min" value={`${overview.response_speed.fast_rate_under_5min_pct}%`}
            sub={`Médiane : ${formatDuration(overview.response_speed.median_response_seconds)}`}
            highlight={overview.response_speed.fast_rate_under_5min_pct >= 70 ? "good" : overview.response_speed.fast_rate_under_5min_pct >= 40 ? "warn" : "bad"}
          />
          <KpiCard
            icon={Bot} color="#a855f7"
            label="Auto IA" value={`${overview.ai.automation_rate_pct}%`}
            sub={`${overview.ai.auto_replied + overview.ai.human_approved} sur ${overview.volume.outbound}`}
          />
          <KpiCard
            icon={overview.nps_proxy.score >= 0 ? Smile : Frown}
            color={overview.nps_proxy.score >= 30 ? "#22c55e" : overview.nps_proxy.score >= 0 ? "#f59e0b" : "#f43f5e"}
            label="NPS proxy" value={overview.nps_proxy.score >= 0 ? `+${overview.nps_proxy.score}` : overview.nps_proxy.score}
            sub={`${overview.nps_proxy.positive_intents} pos · ${overview.nps_proxy.negative_intents} neg`}
          />
          <KpiCard
            icon={TrendingUp} color="#f59e0b"
            label="Threads actifs" value={overview.volume.active_threads}
            sub="sur la période"
          />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        {/* Response speed buckets */}
        {overview && (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 lg:col-span-1" data-testid="response-buckets">
            <h3 className="text-xs font-bold uppercase text-slate-400 mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Vitesse de réponse</h3>
            <SpeedBars buckets={overview.response_speed.buckets} />
          </div>
        )}

        {/* Channels distribution */}
        {overview && (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 lg:col-span-1" data-testid="channels-distribution">
            <h3 className="text-xs font-bold uppercase text-slate-400 mb-3">Répartition par canal</h3>
            <ChannelBars data={overview.volume.by_channel} total={overview.volume.total_messages} />
          </div>
        )}

        {/* Top intents */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 lg:col-span-1" data-testid="intents-card">
          <h3 className="text-xs font-bold uppercase text-slate-400 mb-3 flex items-center gap-2"><Bot className="w-3.5 h-3.5" /> Top intents IA</h3>
          {intents.length === 0 ? (
            <p className="text-[11px] text-slate-500">Aucune donnée IA encore.</p>
          ) : (
            <ul className="space-y-2">
              {intents.slice(0, 6).map((it) => {
                const color = INTENT_COLORS[it.category] || "#94a3b8";
                const label = INTENT_LABELS[it.category] || it.category;
                const max = intents[0]?.total || 1;
                const pct = (it.total / max) * 100;
                return (
                  <li key={it.category}>
                    <div className="flex items-center justify-between mb-0.5 text-[11px]">
                      <span style={{ color }}>{label}</span>
                      <span className="text-slate-400 font-mono">{it.total}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Heatmap */}
      {heatmap && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-5" data-testid="heatmap-card">
          <h3 className="text-xs font-bold uppercase text-slate-400 mb-3 flex items-center gap-2">
            🔥 Heatmap horaire — quand vos clients écrivent
          </h3>
          <Heatmap grid={heatmap.grid} days={heatmap.days} peak={heatmap.peak} />
          <p className="text-[10px] text-slate-500 mt-2">
            UTC. Plus la case est claire, plus le volume est élevé. Optimisez vos heures de présence en fonction.
          </p>
        </div>
      )}

      {/* Top senders */}
      {senders.length > 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-5" data-testid="top-senders">
          <h3 className="text-xs font-bold uppercase text-slate-400 mb-3">Top contacts (par messages reçus)</h3>
          <ul className="space-y-1.5">
            {senders.map((s, i) => {
              const bg = CHANNEL_BG[s.channel] || "#64748b";
              return (
                <li key={`${s.channel}-${s.sender_id}-${i}`} className="flex items-center gap-2 p-2 rounded hover:bg-white/5">
                  <span className="text-[10px] text-slate-500 w-5">{i + 1}.</span>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: bg }}>
                    {(s.sender_name || s.sender_id || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{s.sender_name || s.sender_id}</div>
                    <div className="text-[10px] text-slate-500 capitalize">{s.channel}</div>
                  </div>
                  <span className="text-xs font-bold text-violet-300">{s.count} msg</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sub, highlight }) {
  const ringClass = highlight === "good" ? "ring-1 ring-emerald-500/40" :
    highlight === "warn" ? "ring-1 ring-amber-500/40" :
    highlight === "bad" ? "ring-1 ring-rose-500/40" : "";
  return (
    <div className={`bg-[#14101c] border border-white/5 rounded-xl p-3 ${ringClass}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-4 h-4 mb-1.5" style={{ color }} />
      <div className="text-xl font-black" style={{ color }}>{value}</div>
      <div className="text-[10px] text-slate-500">{label}</div>
      {sub && <div className="text-[9px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function SpeedBars({ buckets }) {
  const total = Object.values(buckets).reduce((a, b) => a + b, 0) || 1;
  const items = [
    { key: "under_5min", label: "< 5 min", color: "#22c55e" },
    { key: "under_15min", label: "5–15 min", color: "#84cc16" },
    { key: "under_1h", label: "15–60 min", color: "#f59e0b" },
    { key: "over_1h", label: "> 1 h", color: "#f43f5e" },
    { key: "no_response", label: "Sans réponse", color: "#64748b" },
  ];
  return (
    <ul className="space-y-2">
      {items.map((it) => {
        const v = buckets[it.key] || 0;
        const pct = (v / total) * 100;
        return (
          <li key={it.key}>
            <div className="flex justify-between text-[11px] mb-0.5">
              <span style={{ color: it.color }}>{it.label}</span>
              <span className="text-slate-400 font-mono">{v} ({pct.toFixed(0)}%)</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: it.color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ChannelBars({ data, total }) {
  const items = [
    { key: "whatsapp", label: "WhatsApp", color: "#25d366" },
    { key: "messenger", label: "Messenger", color: "#0084ff" },
    { key: "instagram", label: "Instagram", color: "#e1306c" },
  ];
  return (
    <ul className="space-y-3">
      {items.map((it) => {
        const v = data[it.key] || 0;
        const pct = total > 0 ? (v / total) * 100 : 0;
        return (
          <li key={it.key}>
            <div className="flex justify-between text-[11px] mb-1">
              <span style={{ color: it.color }} className="font-bold">{it.label}</span>
              <span className="text-slate-400 font-mono">{v} ({pct.toFixed(0)}%)</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: it.color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Heatmap({ grid, days, peak }) {
  return (
    <div className="overflow-x-auto" data-testid="heatmap-grid">
      <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: `auto repeat(24, minmax(20px, 1fr))` }}>
        {/* Header hours */}
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-[8px] text-slate-500 text-center">{h}</div>
        ))}
        {/* Rows */}
        {grid.map((row, r) => (
          <>
            <div key={`d-${r}`} className="text-[10px] text-slate-400 pr-1.5 text-right">{days[r]}</div>
            {row.map((v, h) => {
              const intensity = peak > 0 ? v / peak : 0;
              const bg = v === 0 ? "rgba(255,255,255,0.03)" : `rgba(168, 85, 247, ${0.15 + intensity * 0.85})`;
              return (
                <div
                  key={`${r}-${h}`}
                  className="aspect-square rounded-sm border border-white/5 hover:ring-1 hover:ring-violet-400 transition-all"
                  style={{ backgroundColor: bg }}
                  title={`${days[r]} ${h}h — ${v} messages`}
                />
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}
